import {registerSyncMessageServiceWorker} from '@cip/code-runner';
import {reportSyncMessageStartupError} from 'utils/reportSyncMessageStartupError';
import {clearFailedPageLoads} from 'utils/syncMessageFailedPageLoads';

let registrationPromise: Promise<void> | null = null;
const STARTUP_REGISTRATION_RETRY_DELAY_MS = 300;
const STARTUP_REGISTRATION_MAX_ATTEMPTS = 2;
const CONTROLLER_ERROR_SNIPPET = 'not controlling this page';
// Firefox's catch-all when the worker never comes up, including when it cannot
// launch the worker's process while an installed update awaits a restart.
const GECKO_SCRIPT_EVALUATION_SNIPPET =
  'threw an exception during script evaluation';
// Chromium register() rejections that only site-data settings or storage can
// fix. The AbortError is its kErrorAbort status, which the service-worker
// registry returns when its storage is disabled or being rebuilt after a
// database error. The NotSupportedError is raised when the site's cookie and
// site-data setting refuses service workers. Chromium's other AbortErrors and
// NotSupportedErrors carry different text.
const STORAGE_ERRORS: ReadonlyArray<{name: string; snippet: string}> = [
  {name: 'AbortError', snippet: 'operation has been aborted'},
  {name: 'NotSupportedError', snippet: 'user denied permission to use service worker'},
];

/** Classified cause of a sync-message service-worker startup failure. */
export type SyncMessageServiceWorkerErrorKind =
  | 'controller_unavailable'
  | 'unsupported'
  | 'storage_unavailable'
  | 'script_evaluation_failed'
  | 'unknown';

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function registerWithStartupRetry(): Promise<void> {
  let lastError: unknown = null;
  for (let attempt = 1; attempt <= STARTUP_REGISTRATION_MAX_ATTEMPTS; attempt++) {
    try {
      await registerSyncMessageServiceWorker({
        mode: 'startup',
        // Telemetry only, no user-facing prompt.
        onUpdateTimeout: ({waitedMs}) => {
          void reportSyncMessageStartupError(
            'service-worker-update-timeout',
            new Error(`registration.update() did not settle within ${waitedMs}ms`),
          );
        },
      });
      return;
    } catch (error) {
      lastError = error;
      if (attempt >= STARTUP_REGISTRATION_MAX_ATTEMPTS) {
        throw error;
      }
      await sleep(STARTUP_REGISTRATION_RETRY_DELAY_MS);
    }
  }
  throw lastError;
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}

/**
 * Classifies a sync-message service-worker startup failure so the caller can
 * pick recovery advice that fits it.
 *
 * @param error The rejection from registration or readiness; any value.
 * @return The matching kind, or `'unknown'` when nothing more specific fits.
 * @throws Never.
 */
export function getSyncMessageServiceWorkerErrorKind(
  error: unknown,
): SyncMessageServiceWorkerErrorKind {
  const message = getErrorMessage(error).toLowerCase();

  if (message.includes(CONTROLLER_ERROR_SNIPPET)) {
    return 'controller_unavailable';
  }
  if (message.includes('not supported')) {
    return 'unsupported';
  }
  const name =
    typeof error === 'object' && error !== null && 'name' in error
      ? error.name
      : undefined;
  if (
    STORAGE_ERRORS.some(
      (storageError) =>
        storageError.name === name && message.includes(storageError.snippet),
    )
  ) {
    return 'storage_unavailable';
  }
  if (message.includes(GECKO_SCRIPT_EVALUATION_SNIPPET)) {
    return 'script_evaluation_failed';
  }
  return 'unknown';
}

export function ensureSyncMessageServiceWorkerReady(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    return Promise.reject(
      new Error('Service workers are not supported in this browser.'),
    );
  }

  if (!registrationPromise) {
    registrationPromise = registerWithStartupRetry()
      .then(() => {
        clearFailedPageLoads();
      })
      .catch((error) => {
        registrationPromise = null;
        throw error;
      });
  }

  return registrationPromise;
}
